import { useMemo, useState, type FormEvent } from 'react';
import {
  Badge,
  Button,
  EmptyState,
  SectionHeading,
  Surface,
  eyebrowStyle,
  inputStyle,
  pageStyle,
} from '@kilat/ui';

import { useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { useActiveOutlet } from '../lib/use-active-outlet.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { refundsRepoFor } from '../lib/repositories/refunds-repo.js';
import { callDecideRefund, callRequestRefund } from '../lib/callables/refunds.js';
import { logger } from '../lib/logger.js';
import {
  REFUND_TRANSITIONS,
  type RefundReason,
  type RefundRequest,
  type RefundStatus,
} from '@kilat/shared';

/**
 * /refunds — money handed back to customers (G8).
 *
 * Refunds used to happen entirely outside Kilat: cash out of the till, nothing
 * recorded, the day's takings overstated and no answer to "how often, and
 * why". This screen is the record.
 *
 * It deliberately does not move money. Under the zero-cut model the wash cash
 * is already the operator's and Kilat never touches it, so "Mark paid back" is
 * a statement of fact about something that happened at the till or the bank —
 * which is why it asks HOW it was returned rather than offering to do it.
 *
 * Separation of duty is the whole control: a cashier can raise a refund, only
 * an owner or manager can approve one. Nothing else stands between a till and
 * the cash drawer, so the decide buttons are not merely hidden from a cashier —
 * the server refuses them too.
 */

const REASONS: readonly RefundReason[] = [
  'damage',
  'poor_result',
  'overcharged',
  'duplicate_payment',
  'service_not_provided',
  'goodwill',
  'other',
];

const TONE: Record<RefundStatus, 'warn' | 'info' | 'danger' | 'success'> = {
  pending: 'warn',
  approved: 'info',
  rejected: 'danger',
  settled: 'success',
};

export function RefundsPage() {
  const { claims } = useAuth();
  const t = useI18n();
  const { outletId } = useActiveOutlet();

  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;
  const role =
    typeof claims?.['roles'] === 'object' && claims['roles'] !== null
      ? (claims['roles'] as Record<string, string>)[tenantId ?? '']
      : undefined;
  const canDecide = role === 'owner' || role === 'manager';

  const subscribe = useMemo(() => {
    if (!tenantId) return null;
    const repo = refundsRepoFor(tenantId);
    return (cb: (v: RefundRequest[]) => void) => repo.listSnapshot(cb);
  }, [tenantId]);

  const { data: refunds, loading } = useTrackedSnapshot<RefundRequest[]>(
    ['refunds', tenantId ?? 'none'],
    subscribe,
  );

  const [ticketId, setTicketId] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState<RefundReason>('poor_result');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function raise(e: FormEvent) {
    e.preventDefault();
    if (!tenantId || !outletId) return;
    // Ringgit in the field, sen on the wire — the whole system stores sen, and
    // a rounding slip here is a money bug.
    const sen = Math.round(Number.parseFloat(amount) * 100);
    if (!Number.isFinite(sen) || sen <= 0) return;

    setBusy(true);
    setError(null);
    try {
      await callRequestRefund({
        tenantId,
        outletId,
        ticketId: ticketId.trim(),
        amountSen: sen,
        reason,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      setTicketId('');
      setAmount('');
      setNote('');
    } catch (err) {
      // Surface the server's own words: it explains exactly how much of the
      // wash is still refundable, which is more use than a generic failure.
      setError((err as Error).message || t.t('dashboard.refunds.errorGeneric'));
      logger.error('requestRefund failed', { error: String(err) });
    } finally {
      setBusy(false);
    }
  }

  async function decide(refund: RefundRequest, to: RefundStatus) {
    if (!tenantId) return;
    setBusy(true);
    setError(null);
    try {
      await callDecideRefund({
        tenantId,
        refundId: refund.id,
        to,
        // Settling records HOW the money went back. Defaulting to the original
        // method is right far more often than not, and it stays editable.
        ...(to === 'settled' ? { settledMethod: refund.paidMethod } : {}),
      });
    } catch (err) {
      setError((err as Error).message || t.t('dashboard.refunds.errorGeneric'));
      logger.error('decideRefund failed', { error: String(err) });
    } finally {
      setBusy(false);
    }
  }

  const money = useMemo(
    () => new Intl.NumberFormat('en-MY', { style: 'currency', currency: 'MYR' }),
    [],
  );

  const rows = refunds ?? [];

  return (
    <main style={pageStyle}>
      <header style={{ marginBottom: 'var(--space-6)' }}>
        <p style={eyebrowStyle}>{t.t('dashboard.refunds.title')}</p>
        <h1 style={{ fontSize: 'var(--text-h1)', fontWeight: 600, margin: 'var(--space-2) 0 0 0' }}>
          {t.t('dashboard.refunds.title')}
        </h1>
        <p style={{ color: 'var(--color-on-surface-muted)', marginTop: 'var(--space-2)' }}>
          {t.t('dashboard.refunds.subtitle')}
        </p>
      </header>

      <div className="layout-split-aside">
        <Surface>
          <SectionHeading>{t.t('dashboard.refunds.title')}</SectionHeading>

          {loading ? (
            <p style={{ color: 'var(--color-on-surface-muted)' }}>{t.t('common.loading')}</p>
          ) : rows.length === 0 ? (
            <EmptyState
              title={t.t('dashboard.refunds.empty')}
              body={t.t('dashboard.refunds.emptyBody')}
            />
          ) : (
            <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
              {rows.map((r) => (
                <article
                  key={r.id}
                  style={{
                    display: 'grid',
                    gap: 'var(--space-2)',
                    padding: 'var(--space-3)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'baseline',
                      gap: 'var(--space-2)',
                      justifyContent: 'space-between',
                    }}
                  >
                    <strong style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {money.format(r.amountSen / 100)}
                    </strong>
                    <Badge tone={TONE[r.status]}>
                      {t.t(`dashboard.refunds.status.${r.status}`)}
                    </Badge>
                  </div>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 'var(--text-meta)',
                      color: 'var(--color-on-surface-muted)',
                    }}
                  >
                    {t.t(`dashboard.refunds.reason.${r.reason}`)}
                    {r.note ? ` · ${r.note}` : ''}
                  </p>

                  {/* Only offer moves the state machine actually permits, and
                      only to someone the server will accept them from. */}
                  {canDecide && REFUND_TRANSITIONS[r.status].length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                      {REFUND_TRANSITIONS[r.status].includes('approved') ? (
                        <Button
                          variant="primary"
                          disabled={busy}
                          onClick={() => void decide(r, 'approved')}
                        >
                          {t.t('dashboard.refunds.approve')}
                        </Button>
                      ) : null}
                      {REFUND_TRANSITIONS[r.status].includes('rejected') ? (
                        <Button
                          variant="danger"
                          disabled={busy}
                          onClick={() => void decide(r, 'rejected')}
                        >
                          {t.t('dashboard.refunds.reject')}
                        </Button>
                      ) : null}
                      {REFUND_TRANSITIONS[r.status].includes('settled') ? (
                        <Button
                          variant="secondary"
                          disabled={busy}
                          onClick={() => void decide(r, 'settled')}
                        >
                          {t.t('dashboard.refunds.settle')}
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </article>
              ))}
            </div>
          )}
        </Surface>

        <Surface>
          <SectionHeading>{t.t('dashboard.refunds.raise')}</SectionHeading>
          <form onSubmit={(e) => void raise(e)} style={{ display: 'grid', gap: 'var(--space-3)' }}>
            <label style={{ display: 'grid', gap: 'var(--space-1)' }}>
              <span style={{ fontSize: 'var(--text-meta)' }}>
                {t.t('dashboard.refunds.ticketLabel')}
              </span>
              <input
                value={ticketId}
                onChange={(e) => setTicketId(e.target.value)}
                placeholder={t.t('dashboard.refunds.ticketPlaceholder')}
                required
                style={inputStyle}
              />
            </label>

            <label style={{ display: 'grid', gap: 'var(--space-1)' }}>
              <span style={{ fontSize: 'var(--text-meta)' }}>
                {t.t('dashboard.refunds.amountLabel')}
              </span>
              <input
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputMode="decimal"
                required
                style={inputStyle}
              />
            </label>

            <label style={{ display: 'grid', gap: 'var(--space-1)' }}>
              <span style={{ fontSize: 'var(--text-meta)' }}>
                {t.t('dashboard.refunds.reasonLabel')}
              </span>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value as RefundReason)}
                style={inputStyle}
              >
                {REASONS.map((r) => (
                  <option key={r} value={r}>
                    {t.t(`dashboard.refunds.reason.${r}`)}
                  </option>
                ))}
              </select>
            </label>

            <label style={{ display: 'grid', gap: 'var(--space-1)' }}>
              <span style={{ fontSize: 'var(--text-meta)' }}>
                {t.t('dashboard.refunds.noteLabel')}
              </span>
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={500}
                style={inputStyle}
              />
            </label>

            {error ? (
              <p role="alert" style={{ color: 'var(--color-danger)', margin: 0 }}>
                {error}
              </p>
            ) : null}

            <Button type="submit" variant="primary" disabled={busy}>
              {busy ? t.t('dashboard.refunds.submitting') : t.t('dashboard.refunds.submit')}
            </Button>

            {!canDecide ? (
              // Say it up front rather than letting a cashier raise one and
              // wonder why no approve button ever appears.
              <p
                style={{
                  margin: 0,
                  fontSize: 'var(--text-meta)',
                  color: 'var(--color-on-surface-muted)',
                }}
              >
                {t.t('dashboard.refunds.onlyOwnerDecides')}
              </p>
            ) : null}
          </form>
        </Surface>
      </div>
    </main>
  );
}
